import { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { 
  Brain, User, Sparkles, Download, ChevronRight, Grid3x3, 
  ThumbsUp, ThumbsDown, History, Filter, Route, MapPin, Building2, AlertTriangle, Clock, BarChart3, Truck
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { formatTimestamp } from '@/utils/formatDuration';
import { useState, useCallback } from 'react';

import { triggerExcelDownload } from '@/api/ares';
import { useSSEQuery } from '@/hooks/useSSEQuery';
import { useLearningTracking } from '@/hooks/useLearningTracking';
import { useChatStore } from '@/stores/chatStore';
import Badge from '@/components/ui/Badge';
import type { ChatMessage as ChatMessageType, QuickReply } from '@/types/chat';


interface ChatMessageProps {
  message: ChatMessageType;
  previousMessage?: ChatMessageType;
  onContextMenu?: (e: React.MouseEvent, messageId: string) => void;
  isLastFleetMessage?: boolean;
  isLatestMessage?: boolean;
}

const FILTER_LABELS: Record<string, string> = {
  chart_by: 'AGRUPAR POR',
  chart_type: 'TIPO GRÁFICA',
  city: 'CIUDAD',
  comparisons: 'COMPARACIÓN',
  customer: 'CLIENTE',
  delay_status: 'DEMORA',
  destination_name: 'DESTINO',
  highway: 'CARRETERA',
  metric: 'MÉTRICA',
  poi: 'PUNTO CLAVE',
  remaining_distance_km: 'DISTANCIA REST. (KM)',
  risk_level: 'RIESGO',
  sort_by: 'ORDENAR POR',
  state: 'UBICACIÓN',
};

const FILTER_VALUE_LABELS: Record<string, string> = {
  average_speed: 'Velocidad promedio',
  bar: 'Barras',
  count: 'Conteo',
  desc: 'Mayor a menor',
  distance_between: 'Distancia entre viajes',
  high: 'Alto',
  late: 'Retrasado',
  low: 'Bajo',
  medium: 'Medio',
  on_time: 'A tiempo',
  pie: 'Pastel',
  radar: 'Radar',
  risk: 'Riesgo',
  scatter: 'Dispersión',
  state: 'Estado',
};

const INTERNAL_FILTER_KEYS = new Set([
  'entities',
  'comparands',
  'compare_groups',
]);

function toTitleCase(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatSimpleFilterValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  const normalized = raw.trim().toLowerCase();
  return FILTER_VALUE_LABELS[normalized] || toTitleCase(raw);
}

function summarizeComparisonItem(item: unknown): string {
  if (!item || typeof item !== 'object') {
    return formatSimpleFilterValue(item);
  }

  const data = item as Record<string, unknown>;
  const parts = [
    data.customer,
    data.transport_line,
    data.state,
    data.city,
    data.destination_name,
  ]
    .filter((part) => part !== null && part !== undefined && String(part).trim() !== '')
    .map((part) => formatSimpleFilterValue(part));

  const ranking = data.sort_by
    ? `mayor ${formatSimpleFilterValue(data.sort_by).toLowerCase()}`
    : '';

  return [...parts, ranking].filter(Boolean).join(' · ');
}

function formatFilterValue(key: string, value: unknown): string {
  if (Array.isArray(value)) {
    if (key === 'comparisons') {
      return value.map(summarizeComparisonItem).filter(Boolean).join(' vs ');
    }
    return value.map((item) => formatFilterValue(key, item)).filter(Boolean).join(', ');
  }

  if (value && typeof value === 'object') {
    const data = value as Record<string, unknown>;
    if ('alias' in data || 'description' in data || 'label' in data) {
      return formatSimpleFilterValue(data.alias || data.description || data.label);
    }

    const entries = Object.entries(data);
    const opMap: Record<string, string> = { lt: '<', gt: '>', lte: '<=', gte: '>=', eq: '=' };
    return entries
      .map(([op, val]) => `${opMap[op] || toTitleCase(op)} ${formatSimpleFilterValue(val)}`)
      .join(', ');
  }

  return formatSimpleFilterValue(value);
}

function filterTitle(key: string): string {
  return FILTER_LABELS[key] || key.replace(/_/g, ' ').toUpperCase();
}

function filterIcon(key: string) {
  switch (key) {
    case 'state':
    case 'city':
    case 'destination_name':
    case 'poi':
      return <MapPin size={9} />;
    case 'customer':
    case 'comparisons':
      return <Building2 size={9} />;
    case 'risk_level':
      return <AlertTriangle size={9} />;
    case 'highway':
    case 'remaining_distance_km':
    case 'metric':
      return <Route size={9} />;
    case 'delay_status':
      return <Clock size={9} />;
    case 'chart_by':
    case 'chart_type':
      return <BarChart3 size={9} />;
    default:
      return null;
  }
}

// ── Feedback inline (icono único dentro de la pill unificada) ────────────────
function InlineFeedback({ query, response, messageId }: { query: string; response: string; messageId: string }) {
  const { trackExplicitFeedback } = useLearningTracking();
  const markMessageAsRated = useChatStore((s) => s.markMessageAsRated);
  const cancelDwellTracking = useChatStore((s) => s.cancelDwellTracking);
  const [sent, setSent] = useState<'positive' | 'negative' | null>(null);

  const vote = useCallback((positive: boolean) => {
    if (sent) return;
    cancelDwellTracking(messageId);
    markMessageAsRated(messageId);
    trackExplicitFeedback(query, response.substring(0, 200), positive);
    setSent(positive ? 'positive' : 'negative');
  }, [sent, query, response, messageId, trackExplicitFeedback, markMessageAsRated, cancelDwellTracking]);

  if (sent) {
    return (
      <span className="flex items-center gap-1 px-3 text-[10px] text-text-subtle flex-shrink-0">
        {sent === 'positive' ? '👍' : '👎'}
        <span>Gracias</span>
      </span>
    );
  }

  return (
    <div className="flex items-center gap-0.5 px-2 flex-shrink-0">
      <span className="text-[9px] text-text-subtle mr-1 hidden sm:block">¿Útil?</span>
      <button
        onClick={() => vote(true)}
        className="w-6 h-6 rounded-lg flex items-center justify-center text-text-subtle
                   hover:text-status-success hover:bg-status-success/10 transition-all"
        title="Sí, fue útil"
      >
        <ThumbsUp size={12} />
      </button>
      <button
        onClick={() => vote(false)}
        className="w-6 h-6 rounded-lg flex items-center justify-center text-text-subtle
                   hover:text-status-error hover:bg-status-error/10 transition-all"
        title="No fue útil"
      >
        <ThumbsDown size={12} />
      </button>
    </div>
  );
}

function ScopeQuickReplies({ replies }: { replies: QuickReply[] }) {
  const { sendQuery } = useSSEQuery();
  const isLoading = useChatStore((s) => s.isLoading);

  const handleReply = useCallback((reply: QuickReply) => {
    if (isLoading) return;
    sendQuery(reply.query, {
      displayText: reply.label,
      clarificationChoice: reply.choice,
    });
  }, [isLoading, sendQuery]);

  return (
    <div className="self-start w-full max-w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
      {replies.map((reply) => {
        const isWorkingSet = reply.choice === 'working_set';
        return (
          <button
            key={`${reply.choice || reply.query}-${reply.label}`}
            type="button"
            onClick={() => handleReply(reply)}
            disabled={isLoading}
            title={isWorkingSet ? 'Usar viajes filtrados' : 'Usar toda la flota'}
            className={`group/reply min-h-[72px] rounded-xl border px-3.5 py-2.5 text-left shadow-sm transition-all duration-200
                        active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 ${
                          isWorkingSet
                            ? 'border-accent-blue/22 bg-gradient-to-br from-accent-blue/10 via-accent-blue/5 to-bg-secondary hover:border-accent-blue/40 hover:shadow-accent-blue/10'
                            : 'border-accent-purple/22 bg-gradient-to-br from-accent-purple/10 via-accent-purple/5 to-bg-secondary hover:border-accent-purple/40 hover:shadow-accent-purple/10'
                        }`}
          >
            <div className="flex items-start gap-2.5">
              <div
                className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                  isWorkingSet
                    ? 'bg-accent-blue/14 text-accent-blue'
                    : 'bg-accent-purple/14 text-accent-purple'
                }`}
              >
                {isWorkingSet ? <Filter size={15} /> : <Truck size={15} />}
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-bold text-text-primary">{reply.label}</div>
                <div className="mt-0.5 text-[10px] leading-relaxed text-text-muted">
                  {isWorkingSet
                    ? 'Usar el conjunto actual que ya está filtrado.'
                    : 'Hacer la consulta sobre toda la flota disponible.'}
                </div>
              </div>
            </div>
          </button>
        );
      })}
      </div>
    </div>
  );
}

function ChatMessageComponent({
  message,
  previousMessage,
  onContextMenu,
  isLastFleetMessage = false,
  isLatestMessage = false,
}: ChatMessageProps) {
  const displayName = useAuthStore((s) => s.getUserDisplayName)();

  const isUser = message.sender === 'user';
  const time = formatTimestamp(message.timestamp);
  const perfMs = message.processingTime;

  const query = !isUser && previousMessage?.sender === 'user' 
    ? previousMessage.content 
    : '';

  const hasDataActions = message.exportId || message.chart || (message.records && message.records.length > 0);
  const showQuickReplies = (
    !isUser
    && isLatestMessage
    && message.clarificationType === 'scope'
    && Boolean(message.quickReplies?.length)
  );

  return (
    <div
      id={`message-${message.id}`}
      className={`flex flex-col gap-2 message-enter relative ${isUser ? 'items-end' : ''}`}
      onContextMenu={(e) => onContextMenu?.(e, message.id)}
    >
      {/* Header */}
      <div className={`flex items-center gap-2.5 ${isUser ? 'flex-row-reverse' : ''}`}>
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0 ${isUser
              ? 'glass text-text-secondary'
              : 'bg-gradient-to-br from-accent-blue via-accent-purple to-accent-blue/80 text-white shadow-lg shadow-accent-blue/20'
            }`}
        >
          {isUser ? <User size={16} /> : <Brain size={16} />}
        </div>
        <span className="text-xs font-semibold text-text-secondary">
          {isUser ? displayName : 'Ares'}
        </span>
        {!isUser && (
          <Sparkles size={12} className="text-accent-purple animate-pulse-soft" />
        )}
        <span className="text-[11px] text-text-subtle font-mono flex items-center gap-1.5 ml-auto">
          {perfMs != null && (
            <Badge variant={perfMs > 10000 ? 'warning' : 'success'}>
              {(perfMs / 1000).toFixed(1)}s
            </Badge>
          )}
          {time}
        </span>
      </div>

      {/* Context Legend (Filters) */}
      {!isUser && message.active_filters && Object.keys(message.active_filters).length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mb-1.5 px-0.5 pointer-events-none select-none">
          <div className="text-[9px] font-bold text-text-subtle/60 uppercase tracking-tighter mr-0.5">FILTRADO:</div>
          {Object.entries(message.active_filters).map(([key, data]) => {
            const value = typeof data === 'object' && data !== null && 'value' in data ? data.value : data;
            const isInherited = typeof data === 'object' && data !== null && 'inherited' in data ? data.inherited : false;
            
            if (
              value === null ||
              value === undefined ||
              value === '' ||
              key.startsWith('_') ||
              INTERNAL_FILTER_KEYS.has(key)
            ) return null;

            const displayValue = formatFilterValue(key, value);
            if (!displayValue) return null;

            const icon = filterIcon(key);
            const title = filterTitle(key);

            return (
              <div 
                key={key} 
                className={`flex items-center gap-1 text-[10px] max-w-full ${isInherited ? 'bg-accent-blue/5 border-accent-blue/20' : 'bg-bg-secondary/50 border-border-subtle'} px-1.5 py-0.5 rounded border transition-all`}
                title={displayValue}
              >
                {isInherited && <History size={8} className="text-accent-blue/60" />}
                <span className="font-bold text-text-subtle/80 text-[9px] shrink-0">{title}</span>
                <span className="w-px h-2 bg-border-subtle/50" />
                <span className="flex items-center gap-0.5 text-text-primary font-semibold min-w-0">
                  {icon}
                  <span className="truncate max-w-[220px] sm:max-w-[280px]">{displayValue}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Reasoning */}
      {message.reasoning && !isUser && (
        <div className="relative scroll-reveal">
          <div className="glass-card border-accent-purple/20 rounded-lg px-3 py-2.5 text-[11px] leading-relaxed text-text-muted">
            <div className="font-semibold text-accent-purple mb-1.5 text-[10px] uppercase tracking-wider flex items-center gap-1.5">
              <Brain size={10} />
              Analizando...
            </div>
            {message.reasoning}
          </div>
        </div>
      )}

      {/* Content */}
      <div
        className={`border rounded-xl px-3 py-2 text-xs leading-relaxed max-w-full overflow-wrap-break-word ${isUser
            ? 'bg-gradient-to-br from-accent-blue to-accent-purple border-transparent text-white shadow-lg shadow-accent-blue/25'
            : 'glass-card border-white/5 text-text-secondary'
          }`}
      >
        {isUser ? (
          message.content
        ) : (
          <div className="markdown-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {message.content}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {showQuickReplies && (
        <ScopeQuickReplies replies={message.quickReplies!} />
      )}

      {/* ── Action Bar unificado (solo mensajes IA) ── */}
      {!isUser && (query || hasDataActions) && (
        <div className="flex flex-wrap items-center self-start mt-1 min-h-8 max-w-full
                        bg-bg-secondary border border-border-subtle rounded-2xl
                        overflow-visible shadow-sm">

          {/* SECCIÓN 1: Feedback */}
          {query && <InlineFeedback query={query} response={message.content} messageId={message.id} />}

          {/* Divisor inicial (si hay query y alguna acción) */}
          {query && hasDataActions && (
            <div className="w-px h-4 bg-border-subtle flex-shrink-0" />
          )}

          {/* SECCIÓN 2: Excel */}
          {message.exportId && (
            <button
              onClick={() => triggerExcelDownload(message.exportId!)}
              className="group/dl flex items-center gap-1.5 h-8 px-3
                         text-text-muted hover:text-accent-blue hover:bg-accent-blue/8
                         transition-all duration-150 active:scale-95 flex-shrink-0"
            >
              <Download size={12} className="group-hover/dl:translate-y-[1px] transition-transform text-accent-blue/60 group-hover/dl:text-accent-blue" />
              <span className="text-[10px] font-bold">Excel</span>
            </button>
          )}

          {/* Divisor */}
          {message.exportId && isLastFleetMessage && (message.records?.length ?? 0) > 0 && (
            <div className="w-px h-4 bg-border-subtle flex-shrink-0" />
          )}

          {/* SECCIÓN 3: Flota */}
          {isLastFleetMessage && message.records && message.records.length > 0 && (
            <button
              onClick={() => useUIStore.getState().setFleetPanelOpen(true, message.records)}
              className="group/fl flex items-center gap-1.5 h-8 pl-2.5 pr-3
                         hover:bg-accent-purple/10 transition-all duration-150 active:scale-95 flex-shrink-0"
            >
              <div className="w-4 h-4 rounded-full bg-accent-purple/20 flex items-center justify-center
                              group-hover/fl:bg-accent-purple/40 transition-colors flex-shrink-0">
                <Grid3x3 size={9} className="text-accent-purple" />
              </div>
              <span className="text-[10px] font-black text-accent-purple">
                {message.records.length.toLocaleString()} unidades
              </span>
              <ChevronRight size={11} className="text-accent-purple/40 group-hover/fl:translate-x-0.5 group-hover/fl:text-accent-purple transition-all" />
            </button>
          )}

          {/* Divisor antes de gráfica */}
          {message.chart && (message.exportId || (isLastFleetMessage && (message.records?.length ?? 0) > 0)) && (
            <div className="w-px h-4 bg-border-subtle flex-shrink-0" />
          )}

          {/* SECCIÓN 4: Gráfica */}
          {message.chart && (
            <button
              onClick={() => {
                useChatStore.getState().setCurrentChart(message.chart!);
                useUIStore.getState().setChartModalOpen(true);
              }}
              className="group/ch flex items-center gap-1.5 h-8 px-3
                         hover:bg-accent-blue/10 transition-all duration-150 active:scale-95 flex-shrink-0"
            >
              <div className="w-4 h-4 rounded-full bg-accent-blue/20 flex items-center justify-center
                              group-hover/ch:bg-accent-blue/40 transition-colors flex-shrink-0">
                <BarChart3 size={9} className="text-accent-blue" />
              </div>
              <span className="text-[10px] font-black text-accent-blue uppercase tracking-tight">
                Ver Gráfica
              </span>
            </button>
          )}

        </div>
      )}

    </div>
  );
}

export default memo(ChatMessageComponent);
